import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Paper, Chip, CircularProgress,
  IconButton, Tooltip, Divider, Grid, Button, Icon,
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import { invokeApig } from '../libs/awsLib';
import { useContentManager } from '../hooks/useContentManager';
import { getSectionType } from '../config/contentTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: theme.spacing(3),
    borderRadius: 0,
  },
  topBarTitle: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  navGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: '#f0f0f0',
    borderRadius: 20,
    padding: '2px 8px',
  },
  navLabel: {
    fontSize: 13,
    fontWeight: 600,
    minWidth: 60,
    textAlign: 'center',
    color: '#424242',
  },
  templatePaper: {
    padding: theme.spacing(4),
    maxWidth: 780,
    margin: '0 auto',
  },
  centered: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: '#9e9e9e',
  },
  fieldLabel: {
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontSize: 11,
    fontWeight: 600,
    display: 'block',
    marginBottom: 4,
  },
}));


// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidImage(url) {
  return url && typeof url === 'string' &&
    (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:'));
}


// ─── Templates ───────────────────────────────────────────────────────────────

function VisualCardTemplate({ item, fields }) {
  const imgField = fields.find((f) => f.type === 'image');
  const imgUrl   = imgField ? item[imgField.key] : null;
  const titulo   = item.titulo || item.nombre || '';
  const desc     = item.descripcion || item.description || '';
  const badge    = item.badge || item.categoria || '';
  const persona  = item.persona || item.autor || '';
  const url      = item.url || '';
  const btnTxt   = item.texto_boton || 'Ver más';

  return (
    <div>
      {imgUrl && isValidImage(imgUrl) && (
        <div style={{ borderRadius: 8, overflow: 'hidden', maxHeight: 400, marginBottom: 24 }}>
          <img src={imgUrl} alt={titulo} style={{ width: '100%', objectFit: 'cover', maxHeight: 400, display: 'block' }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        {badge && (
          <Chip label={badge} size="small"
            style={{ backgroundColor: '#ede7f6', color: '#4a148c', fontWeight: 600 }} />
        )}
        {persona && (
          <Typography variant="caption" style={{ color: '#757575', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Person style={{ fontSize: 14 }} /> {persona}
          </Typography>
        )}
      </div>
      {titulo && (
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12 }}>{titulo}</Typography>
      )}
      {desc && (
        <Typography variant="body1" style={{ color: '#424242', lineHeight: 1.85 }}>{desc}</Typography>
      )}
      {url && (
        <div style={{ marginTop: 28 }}>
          <Button variant="contained" color="primary" href={url} target="_blank" rel="noopener noreferrer">
            {btnTxt}
          </Button>
        </div>
      )}
    </div>
  );
}

function ProductTemplate({ item, fields }) {
  const imgUrl     = item.imagen || '';
  const nombre     = item.nombre || '';
  const precio     = item.precio || '';
  const desc       = item.descripcion || '';
  const categoria  = item.categoria || '';
  const disponible = item.disponible;
  const hasImage   = isValidImage(imgUrl);

  return (
    <Grid container spacing={3} alignItems="flex-start">
      {hasImage && (
        <Grid item xs={12} sm={5}>
          <div style={{ borderRadius: 8, overflow: 'hidden', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
            <img src={imgUrl} alt={nombre}
              style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          </div>
        </Grid>
      )}
      <Grid item xs={12} sm={hasImage ? 7 : 12}>
        {categoria && (
          <Chip label={categoria} size="small"
            style={{ marginBottom: 14, backgroundColor: '#e3f2fd', color: '#1565c0' }} />
        )}
        {nombre && (
          <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 8 }}>{nombre}</Typography>
        )}
        {precio && (
          <Typography variant="h4" style={{ color: '#2e7d32', fontWeight: 800, marginBottom: 16 }}>
            {precio}
          </Typography>
        )}
        {disponible !== undefined && disponible !== null && disponible !== '' && (
          <div style={{ marginBottom: 16 }}>
            <Chip
              label={disponible ? 'Disponible' : 'No disponible'}
              style={{
                backgroundColor: disponible ? '#e8f5e9' : '#fce4ec',
                color: disponible ? '#2e7d32' : '#c62828',
                fontWeight: 600,
              }}
            />
          </div>
        )}
        {desc && (
          <Typography variant="body1" style={{ color: '#424242', lineHeight: 1.85 }}>{desc}</Typography>
        )}
      </Grid>
    </Grid>
  );
}

function TeamTemplate({ item }) {
  const foto  = item.foto || item.imagen || '';
  const nombre = item.nombre || '';
  const cargo  = item.cargo || '';
  const desc   = item.descripcion || '';

  return (
    <div style={{ textAlign: 'center' }}>
      {isValidImage(foto) ? (
        <img src={foto} alt={nombre}
          style={{ width: 140, height: 140, borderRadius: '50%', objectFit: 'cover',
            marginBottom: 20, border: '4px solid #ede7f6', display: 'inline-block' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{ width: 140, height: 140, borderRadius: '50%', backgroundColor: '#ede7f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
          <Icons.Person style={{ fontSize: 72, color: '#7b1fa2' }} />
        </div>
      )}
      {nombre && (
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>{nombre}</Typography>
      )}
      {cargo && (
        <Typography variant="subtitle1" style={{ color: '#7b1fa2', marginBottom: 20, fontWeight: 500 }}>
          {cargo}
        </Typography>
      )}
      {desc && (
        <Typography variant="body1"
          style={{ color: '#424242', lineHeight: 1.85, maxWidth: 500, margin: '0 auto' }}>
          {desc}
        </Typography>
      )}
    </div>
  );
}

function TestimonialTemplate({ item }) {
  const foto   = item.foto || '';
  const nombre = item.nombre || '';
  const cargo  = item.cargo || '';
  const texto  = item.texto || '';

  return (
    <div style={{ textAlign: 'center', padding: '0 16px' }}>
      <Icons.FormatQuote style={{ fontSize: 80, color: '#ede7f6', transform: 'scaleX(-1)', display: 'block', margin: '0 auto' }} />
      {texto && (
        <Typography variant="h6"
          style={{ fontStyle: 'italic', color: '#424242', lineHeight: 1.9, marginBottom: 32,
            fontWeight: 400, maxWidth: 600, margin: '0 auto 32px auto' }}>
          "{texto}"
        </Typography>
      )}
      <Divider style={{ marginBottom: 28 }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
        {isValidImage(foto) ? (
          <img src={foto} alt={nombre}
            style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 60, height: 60, borderRadius: '50%', backgroundColor: '#ede7f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Person style={{ color: '#7b1fa2', fontSize: 36 }} />
          </div>
        )}
        <div style={{ textAlign: 'left' }}>
          {nombre && (
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{nombre}</Typography>
          )}
          {cargo && (
            <Typography variant="body2" style={{ color: '#757575' }}>{cargo}</Typography>
          )}
        </div>
      </div>
    </div>
  );
}

function FaqTemplate({ item }) {
  const pregunta  = item.pregunta || '';
  const respuesta = item.respuesta || '';
  const categoria = item.categoria || '';

  return (
    <div>
      {categoria && (
        <Chip label={categoria} size="small"
          style={{ marginBottom: 20, backgroundColor: '#e3f2fd', color: '#1565c0' }} />
      )}
      {pregunta && (
        <Typography variant="h5"
          style={{ fontWeight: 700, color: '#1a237e', marginBottom: 24, lineHeight: 1.5 }}>
          {pregunta}
        </Typography>
      )}
      <Divider style={{ marginBottom: 24 }} />
      {respuesta && (
        <Typography variant="body1" style={{ color: '#424242', lineHeight: 2, fontSize: 16 }}>
          {respuesta}
        </Typography>
      )}
    </div>
  );
}

function GalleryTemplate({ item }) {
  const imagen    = item.imagen || '';
  const titulo    = item.titulo || '';
  const desc      = item.descripcion || '';
  const categoria = item.categoria || '';

  return (
    <div>
      {isValidImage(imagen) && (
        <div style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 20,
          textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <img src={imagen} alt={titulo}
            style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        {categoria && (
          <Chip label={categoria} size="small"
            style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
        )}
      </div>
      {titulo && (
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 8 }}>{titulo}</Typography>
      )}
      {desc && (
        <Typography variant="body1" style={{ color: '#757575' }}>{desc}</Typography>
      )}
    </div>
  );
}

function HeroBannerTemplate({ item }) {
  const imagen    = item.imagen || '';
  const titulo    = item.titulo || '';
  const subtitulo = item.subtitulo || '';
  const btnTxt    = item.texto_boton || '';
  const btnUrl    = item.url_boton || '';

  return (
    <div>
      <div
        style={{
          position: 'relative',
          borderRadius: 10,
          overflow: 'hidden',
          minHeight: 300,
          backgroundColor: '#1a237e',
          backgroundImage: isValidImage(imagen) ? `url(${imagen})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)' }} />
        <div style={{ position: 'relative', textAlign: 'center', color: '#fff', padding: '56px 40px' }}>
          {titulo && (
            <Typography variant="h3"
              style={{ fontWeight: 800, marginBottom: 14, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
              {titulo}
            </Typography>
          )}
          {subtitulo && (
            <Typography variant="h6" style={{ fontWeight: 400, opacity: 0.9, marginBottom: 28 }}>
              {subtitulo}
            </Typography>
          )}
          {btnTxt && btnUrl && (
            <Button variant="contained" color="primary" size="large"
              href={btnUrl} target="_blank" rel="noopener noreferrer">
              {btnTxt}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoTemplate({ item }) {
  const url    = item.url || '';
  const titulo = item.titulo || '';

  let embedUrl = '';
  const ytMatch    = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (ytMatch)    embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
  else if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return (
    <div>
      {embedUrl ? (
        <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: 8,
          overflow: 'hidden', marginBottom: 16 }}>
          <iframe
            src={embedUrl}
            title={titulo || 'Video'}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : url ? (
        <div style={{ textAlign: 'center', padding: 40, backgroundColor: '#f5f5f5',
          borderRadius: 8, marginBottom: 16 }}>
          <Icons.PlayCircleFilled style={{ fontSize: 64, color: '#bdbdbd' }} />
          <Typography variant="body2" style={{ color: '#757575', marginTop: 12 }}>
            <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
          </Typography>
        </div>
      ) : null}
      {titulo && (
        <Typography variant="h6" style={{ fontWeight: 600 }}>{titulo}</Typography>
      )}
    </div>
  );
}

function GenericTemplate({ item, fields }) {
  const classes = useStyles();
  return (
    <div>
      {fields.map((field) => {
        const value = item[field.key];
        if (value === undefined || value === null || value === '') return null;
        return (
          <div key={field.key} style={{ marginBottom: 22 }}>
            <span className={classes.fieldLabel}>{field.label}</span>
            {field.type === 'image' && isValidImage(String(value)) ? (
              <img src={String(value)} alt={field.label}
                style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6, display: 'block', marginTop: 6 }} />
            ) : field.type === 'boolean' ? (
              <Chip
                label={value ? 'Sí' : 'No'} size="small"
                style={{
                  backgroundColor: value ? '#e8f5e9' : '#fce4ec',
                  color: value ? '#2e7d32' : '#c62828',
                  marginTop: 4,
                }}
              />
            ) : (
              <Typography variant="body1" style={{ marginTop: 4 }}>{String(value)}</Typography>
            )}
            <Divider style={{ marginTop: 18 }} />
          </div>
        );
      })}
    </div>
  );
}

const TEMPLATE_MAP = {
  visual_cards:  VisualCardTemplate,
  card:          VisualCardTemplate,
  service_cards: VisualCardTemplate,
  product_cards: ProductTemplate,
  team_cards:    TeamTemplate,
  testimonials:  TestimonialTemplate,
  faq_accordion: FaqTemplate,
  gallery:       GalleryTemplate,
  hero_banner:   HeroBannerTemplate,
  video:         VideoTemplate,
};


// ─── Componente principal ─────────────────────────────────────────────────────

function ContentDetailPage() {
  const { designId, pageId, sectionId, index } = useParams();
  const classes = useStyles();
  const { loadContent } = useContentManager();

  const [allItems,     setAllItems]     = useState([]);
  const [currentIndex, setCurrentIndex] = useState(parseInt(index, 10) || 0);
  const [sectionMeta,  setSectionMeta]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      const designRes = await invokeApig({
        path:   '/site/design/list',
        method: 'POST',
        body:   { designId },
      });
      const pages = designRes.designs || designRes.pages || [];
      const page  = pages.find((p) => p.pageId === pageId);
      if (!page) throw new Error('Página no encontrada');

      const guidedSections = page.metadata?.guidedSections || [];
      const section = guidedSections.find((s) => s.id === sectionId);
      if (!section) throw new Error('Sección no encontrada');
      setSectionMeta(section);

      const contentRes = await loadContent(designId, pageId);
      const items = Array.isArray(contentRes.content?.[sectionId])
        ? contentRes.content[sectionId]
        : [];
      setAllItems(items);

      if (items.length === 0) setError('Esta sección no tiene contenido aún.');
    } catch (err) {
      setError(err.message || 'Error al cargar el contenido');
    } finally {
      setLoading(false);
    }
  };

  const item            = allItems[currentIndex] || null;
  const sectionTypeDef  = sectionMeta ? getSectionType(sectionMeta.type) : null;
  const TemplateComp    = TEMPLATE_MAP[sectionMeta?.type] || GenericTemplate;
  const fields          = sectionMeta?.fields || [];
  const total           = allItems.length;

  const handlePrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const handleNext = () => setCurrentIndex((i) => Math.min(total - 1, i + 1));

  return (
    <Grid container spacing={2} className={classes.root} justifyContent="center">
      <Grid item xs={12} sm={12} md={11} lg={10}>

        {/* ── Barra superior ─────────────────────────────────────────────── */}
        <Paper className={classes.topBar} elevation={0}>
          <div className={classes.topBarTitle}>
            {sectionTypeDef?.icon && (
              <Icon style={{ color: '#7b1fa2', fontSize: 22 }}>{sectionTypeDef.icon}</Icon>
            )}
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                {sectionMeta?.label || 'Detalle'}
              </Typography>
              {sectionTypeDef && (
                <Typography variant="caption" style={{ color: '#9e9e9e' }}>
                  {sectionTypeDef.label}
                </Typography>
              )}
            </div>
          </div>

          {total > 1 && (
            <div className={classes.navGroup}>
              <Tooltip title="Anterior" enterDelay={400}>
                <span>
                  <IconButton size="small" onClick={handlePrev} disabled={currentIndex === 0}>
                    <Icons.ChevronLeft fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography className={classes.navLabel}>
                {currentIndex + 1} / {total}
              </Typography>
              <Tooltip title="Siguiente" enterDelay={400}>
                <span>
                  <IconButton size="small" onClick={handleNext} disabled={currentIndex === total - 1}>
                    <Icons.ChevronRight fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}

          <Tooltip title="Cerrar pestaña" enterDelay={400}>
            <IconButton size="small" onClick={() => window.close()} style={{ marginLeft: 8 }}>
              <Icons.Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>

        {/* ── Contenido ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : error && !item ? (
          <div className={classes.centered}>
            <Icons.ErrorOutline style={{ fontSize: 56, opacity: 0.3 }} />
            <Typography variant="body1" style={{ marginTop: 12 }}>{error}</Typography>
          </div>
        ) : item ? (
          <Paper className={classes.templatePaper}>
            <TemplateComp item={item} fields={fields} />
          </Paper>
        ) : null}

      </Grid>
    </Grid>
  );
}

export default ContentDetailPage;
